import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { configApi } from '@/api/configApi';
import {
  CONTACT,
  BUSINESS_HOURS,
  UPI_CONFIG,
  WELLNESS_TIPS,
  FULL_BODY_PRICES,
  PACKAGE_DISCOUNT,
  PACKAGE_SIZE,
  WALLET_MAX_PERCENT,
  WALLET_MAX_AMOUNT,
  SPA_LOCATIONS,
} from '@/utils/constants';

export interface SiteConfig {
  contact: { phone: string; email: string; address: string; whatsapp: string };
  business_hours: { days: string; time: string }[];
  upi_config: { receiver_upi_id: string; receiver_name: string; receiver_mobile?: string; currency: string };
  wellness_tips: string[];
  full_body_prices: Record<string, number>;
  package_discount: number;
  package_size: number;
  wallet_max_percent: number;
  wallet_max_amount: number;
  stats: {
    happy_clients: number;
    services_count: number;
    locations_count: number;
    average_rating: number;
  };
  /** False until the live config has arrived. Numbers that would otherwise
   *  flash from a built-in guess to the real figure wait for this. */
  loaded: boolean;
  hero: {
    welcome_text: string;
    heading: string;
    heading2?: string;
    heading3?: string;
    heading4?: string;
    heading5?: string;
    heading6?: string;
    subheading: string;
    tagline: string;
  };
  section_headings: Record<string, string>;
  banners: {
    static: {
      label: string;
      title: string;
      subtitle: string;
      cta_text: string;
      image_url: string;
      width?: number;
      height?: number;
    };
    promo: {
      live_label: string;
      title: string;
      subtitle: string;
      cta_text: string;
      link: string;
      meta: string;
      image_url: string;
      width?: number;
      height?: number;
    };
  };
  testimonials: { name: string; detail: string; rating: number; text: string }[];
  pre_payment_message: string;
  survey_config: {
    survey_link: string;
    reward_amount: number;
    form_description: string;
    is_active: boolean;
  };
  logo_url: string;
  /** Ordered fallback photos, pre-cropped to 3:2. A centre's own photos are
   *  padded from the front of this list so every listing renders a full
   *  gallery. Still accepts a bare string from an older config document. */
  default_centre_image?: string | string[];
  default_service_image?: string;
  social_links: Record<string, string>;
  /** Named clinician who reviews condition/recovery pages. Emitted as
   *  MedicalWebPage.reviewedBy + lastReviewed only when set - never a placeholder. */
  medical_reviewer?: { name: string; title?: string; reviewed_on?: string };
  /** Clinic app store URLs for the "Download the app" buttons. Set from
   *  Admin → Site Config; empty => the button shows "Coming soon". */
  clinic_app_android_url?: string;
  clinic_app_ios_url?: string;
  /** Patient app on Google Play: the navbar badge, the QR and the Android
   *  install prompt all point here. */
  user_app_android_url?: string;
}

// Fallback defaults from current constants
const DEFAULTS: SiteConfig = {
  contact: CONTACT,
  business_hours: BUSINESS_HOURS,
  upi_config: {
    receiver_upi_id: UPI_CONFIG.receiverUpiId,
    receiver_name: UPI_CONFIG.receiverName,
    currency: UPI_CONFIG.currency,
  },
  wellness_tips: WELLNESS_TIPS,
  full_body_prices: Object.fromEntries(
    Object.entries(FULL_BODY_PRICES).map(([k, v]) => [String(k), v])
  ),
  package_discount: PACKAGE_DISCOUNT,
  package_size: PACKAGE_SIZE,
  wallet_max_percent: WALLET_MAX_PERCENT,
  wallet_max_amount: WALLET_MAX_AMOUNT,
  stats: {
    happy_clients: 5000,
    services_count: 50,
    locations_count: SPA_LOCATIONS.length,
    average_rating: 4.9,
  },
  loaded: false,
  hero: {
    welcome_text: 'Home Visits + Clinic Care',
    heading: 'Back Pain Relief, Without the Runaround',
    heading2: 'The Right Physio, Right Around the Corner',
    heading3: 'Recovery That Fits Your Schedule and Budget',
    heading4: 'Verified Clinics, Real Physiotherapists, Real Results',
    heading5: 'Recovery Guided Every Step, From Injury to Healing',
    heading6: 'Physiotherapy That Comes to You, If You Need It',
    subheading:
      'Back pain, knee injury, post-surgery recovery, or just want to move better. Find verified physiotherapy clinics near you, compare services, and book instantly.',
    tagline: 'Find. Book. Recover.',
  },
  section_headings: {
    services_title: 'Specialized Clinical Services',
    services_subtitle:
      'Targeted therapies across musculoskeletal, sports, neuro, and preventive care, delivered at Recovry-verified centres.',
    locations_title: 'Centres near you',
    locations_subtitle: 'Browse verified physiotherapy and wellness centres, sorted by your location.',
    how_it_works_title: 'How Recovry works',
    how_it_works_subtitle: 'Three steps from symptom to session.',
  },
  banners: {
    static: {
      label: 'Updated monthly',
      title: 'Working from home? Your back is paying for it.',
      subtitle:
        'Most desk workers develop posture-related back and neck pain within 2 years. Catch it early.',
      cta_text: 'Check posture & desk relief services',
      image_url: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1200&q=80&auto=format&fit=crop',
      width: 1200,
      height: 480,
    },
    promo: {
      live_label: 'Live now',
      title: '30-day posture correction challenge',
      subtitle:
        'Free first consultation with a partner physiotherapist. Limited slots across PAN India clinics this month.',
      cta_text: 'Claim free consultation',
      link: '/centres',
      meta: 'Ends in 12 days · 214 slots claimed',
      image_url: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1200&q=80&auto=format&fit=crop',
      width: 1200,
      height: 480,
    },
  },
  testimonials: [],
  pre_payment_message:
    'Thanks for choosing Recovry. Your centre will confirm your appointment shortly.',
  survey_config: {
    survey_link: '',
    reward_amount: 50,
    form_description: 'Fill out the form below and get wallet credit!',
    is_active: false,
  },
  logo_url: '/newlogo.png',
  default_centre_image: [
    'https://res.cloudinary.com/dvpf8wrtk/image/upload/v1788024537/1_pjefi7.png',
    'https://res.cloudinary.com/dvpf8wrtk/image/upload/v1788024537/3_ms2jyb.png',
    'https://res.cloudinary.com/dvpf8wrtk/image/upload/v1788024537/2_uum3ho.png',
  ],
  default_service_image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&q=80&auto=format&fit=crop',
  social_links: { facebook: '#', instagram: '#', twitter: '#', youtube: '#' },
  clinic_app_android_url: '',
  clinic_app_ios_url: '',
  user_app_android_url: 'https://play.google.com/store/apps/details?id=in.recovry.app',
};

const SiteConfigContext = createContext<SiteConfig>(DEFAULTS);

declare global {
  interface Window {
    /** Live config baked into the prerendered page, so the first paint
     *  already shows real figures instead of the built-in guesses. */
    __SITE_CONFIG__?: Partial<SiteConfig>;
    /** Live figures the home page computed, baked in the same way. */
    __HOME_STATS__?: { locations?: number | null; rating?: number | null };
    __PRERENDER__?: boolean;
  }
}

/** The config to start from: the snapshot the prerender baked in, else defaults. */
function initialConfig(): SiteConfig {
  const baked = typeof window !== 'undefined' ? window.__SITE_CONFIG__ : undefined;
  return baked ? { ...DEFAULTS, ...baked, loaded: true } : DEFAULTS;
}

export function SiteConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<SiteConfig>(initialConfig);

  useEffect(() => {
    configApi
      .getSiteConfig()
      .then((res) => {
        const data = res.data?.config;
        if (data) {
          const next = { ...DEFAULTS, ...data, loaded: true };
          setConfig(next);
          // Hand the prerender the live config so it can bake it into the page.
          if (typeof window !== 'undefined' && window.__PRERENDER__) {
            window.__SITE_CONFIG__ = data;
          }
        }
      })
      .catch(() => {
        // Keep the defaults, but stop holding figures back: an unreachable
        // API should not leave the page half blank.
        setConfig((c) => ({ ...c, loaded: true }));
      });
  }, []);

  return (
    <SiteConfigContext.Provider value={config}>{children}</SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  return useContext(SiteConfigContext);
}
